import OwnerPageList from "containers/owner/page/OwnerPageList";
import React, { useState, useEffect, useMemo } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from 'react-select';
import { get } from "aws-amplify/api";
import { createElementIdFactory } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";

function OwnerPageSearch({
    isAuthenticated,
    userPermissions,
    controlType,
    showAddButton,
    properties,
    saleid,
    saveRecord,
    buyerSellers,
    existingOwnerParents,
    onOwnerParentAdded,
    refreshDataSet,
    updateTableState: externalUpdateTableState,
    ownerid,
    user,
    onClose,
}) {

    const ownerSearchSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: 38,
            height: 38,
        }),
        valueContainer: (base) => ({
            ...base,
            height: 38,
            padding: "0 8px",
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: 38,
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: 6,
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: 6,
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
    };

    const [isLoading, setIsLoading] = useState(true);
    const [owners, setOwners] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const iscompanyOptions = [
        { label: "Companies & Individuals", value: null },
        { label: "Owner is a company", value: true },
        { label: "Owner is an individual", value: false }
    ];
    const [cityscopeOptions, setCityscopeOptions] = useState([]);
    const [formData, setFormData] = useState({
        selectedCityscope: [],
        property_key: "",
        name: "",
        acn: "",
        selectedIsCompanyOption: { label: "Companies & Individuals", value: null }
    });
    const hasTrustProof = userPermissions && userPermissions.trust_proof === true;
    const isReadyToRender = isAuthenticated && (userPermissions ? hasTrustProof : true);
    const createId = useMemo(() => createElementIdFactory("owner-page-search"), []);

    const updateTableState = (updatedData, dataSet) => {
        if (dataSet === "buyerSellers" && typeof externalUpdateTableState === "function") {
            externalUpdateTableState(updatedData, dataSet);
            return;
        }

        setOwners(updatedData);
    };

    useEffect(() => {
        if (!isAuthenticated) return;

        document.title = "Cityscope Owner Search";
        (async () => {
            setIsLoading(true);
            try {
                const { body } = await get({ apiName: "colin", path: "/cityscopes/dropdown" }).response;
                const payload = await body.json();
                const localCityscopes = normalizeCollectionResponse(payload, ["cityscopes", "dropdown"]);
                setCityscopeOptions(localCityscopes);
            } catch (error) {
                alert(error + ". Please refresh the page (F5).");
                setCityscopeOptions([]);
            }
            setIsLoading(false);
        })();
    }, [isAuthenticated]);

    function normalizeCollectionResponse(payload, keys = []) {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (!payload || typeof payload !== "object") {
            return [];
        }

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (Array.isArray(payload[key])) {
                return payload[key];
            }
        }

        const fallbackKeys = ["owners", "items", "data", "results", "rows", "records"];
        for (let i = 0; i < fallbackKeys.length; i++) {
            const key = fallbackKeys[i];
            if (Array.isArray(payload[key])) {
                return payload[key];
            }
        }

        return [];
    }

    // --- Data Fetching ---
    async function getOwners(filter) {
        try {
            const { body } = await get({
                apiName: "colin",
                path: "/owners",
                options: { queryParams: filter },
            }).response;
            const payload = await body.json();
            return normalizeCollectionResponse(payload, ["owners"]);
        } catch (error) {
            alert(error + ". Please refresh the page (F5).");
            return [];
        }
    }

    // --- Handlers ---
    const handleLocalInputChange = (e) => {
        const { id, name, value } = e.target;
        const fieldName = name || id;
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleCityscopeChange = (selectedOptions) => {
        setFormData(prev => ({
            ...prev,
            selectedCityscope: selectedOptions
        }));
    };

    const handleSIsCompanyChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            selectedIsCompanyOption: selectedOption || { label: "Companies & Individuals", value: null }
        }));
    };

    async function handleSubmit(event) {
        event.preventDefault();
        setIsLoading(true);

        const filter = {
            scope: formData.selectedCityscope ? formData.selectedCityscope.map(r => r.value).join(",") : "",
            propertyid: formData.property_key,
            name: formData.name,
            acn: formData.acn,
            iscompany: formData.selectedIsCompanyOption.value ?? ""
        };

        const results = await getOwners(filter);
        setOwners(results);
        setHasSearched(true);
        setIsLoading(false);
    }

    // --- Render Functions ---
    function renderOwners() {
        const shouldShowAddButton = (controlType || "table") === "table" || showAddButton;

        return (
            <div className="HeadOffice">
                <Row className="show-Container mt-3">
                    <Col sm={12} md={12} lg={12}>
                        <h6>Cityscope Owner Search</h6>

                        <Form onSubmit={handleSubmit}>
                            <Row className="owner-search-filter-row">
                                <Col lg={2} md={4} sm={6} className="mb-2">
                                    <Form.Group controlId={createId("selected-cityscope") }>
                                        <Select
                                            inputId={createId("selected-cityscope")}
                                            value={formData.selectedCityscope}
                                            onChange={handleCityscopeChange}
                                            options={cityscopeOptions}
                                            placeholder="Cityscope (All)"
                                            isMulti
                                            className="owner-search-select"
                                            classNamePrefix="owner-search-select"
                                            styles={ownerSearchSelectStyles}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col lg={2} md={4} sm={6} className="mb-2">
                                    <Form.Group controlId={createId("property-key") }>
                                        <Form.Control
                                            id={createId("property-key")}
                                            name="property_key"
                                            type="text"
                                            value={formData.property_key}
                                            onChange={handleLocalInputChange}
                                            placeholder="Reference starting..."
                                            className="owner-search-input"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col lg={2} md={4} sm={6} className="mb-2">
                                    <Form.Group controlId={createId("name") }>
                                        <Form.Control
                                            id={createId("name")}
                                            name="name"
                                            type="text"
                                            value={formData.name}
                                            onChange={handleLocalInputChange}
                                            placeholder="Owner Name..."
                                            className="owner-search-input"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col lg={2} md={4} sm={6} className="mb-2">
                                    <Form.Group controlId={createId("acn") }>
                                        <Form.Control
                                            id={createId("acn")}
                                            name="acn"
                                            type="text"
                                            value={formData.acn}
                                            onChange={handleLocalInputChange}
                                            placeholder="ACN containing..."
                                            className="owner-search-input"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col lg={2} md={4} sm={6} className="mb-2">
                                    <Form.Group controlId={createId("selected-is-company-option") }>
                                        <Select
                                            inputId={createId("selected-is-company-option")}
                                            value={formData.selectedIsCompanyOption}
                                            onChange={handleSIsCompanyChange}
                                            options={iscompanyOptions}
                                            isClearable
                                            className="owner-search-select"
                                            classNamePrefix="owner-search-select"
                                            styles={ownerSearchSelectStyles}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col lg={2} md={4} sm={6} className="mb-2 d-flex">
                                    <Button id={createId("search-btn")} type="submit" disabled={isLoading} className="owner-search-search-button w-100">
                                        Search
                                    </Button>
                                </Col>
                            </Row>
                        </Form>

                        {!hasSearched && shouldShowAddButton && (
                            <Row className="mt-1">
                                <Col className="text-right">
                                    <Link to="/owners/new">
                                        <Button id={createId("add-btn")} variant="primary" onClick={() => { if (typeof onClose === "function") onClose(); }}>
                                            Add
                                        </Button>
                                    </Link>
                                </Col>
                            </Row>
                        )}

                        {hasSearched && (
                            <Row className="mt-1">
                                <Col>
                                    <OwnerPageList
                                        data={owners || []}
                                        isLoading={isLoading}
                                        isAuthenticated={isAuthenticated}
                                        controlType={controlType || "table"}
                                        showAddButton={showAddButton}
                                        properties={properties}
                                        saleid={saleid}
                                        saveRecord={saveRecord}
                                        buyerSellers={buyerSellers}
                                        existingOwnerParents={existingOwnerParents}
                                        refreshDataSet={refreshDataSet}
                                        onOwnerParentAdded={onOwnerParentAdded}
                                        ownerid={ownerid}
                                        user={user}
                                        updateTableState={updateTableState}
                                        onClose={onClose}
                                    />
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <>
            <div className="OwnerPageSearch">
                {isReadyToRender
                    ? renderOwners()
                    : (isAuthenticated ? <p>Loading permissions...</p> : null)}
            </div>
        </>
    );
}

export default OwnerPageSearch;